import { useRouter } from "next/router";
import { createContext, FC, useContext, useState, useCallback } from "react";
import { ExtendedGraphQLError, sdk } from "~lib";
import { useShowHeaderBanner } from "~components/common/common.HeaderAlertBanner";
import { usePaymentOptionsStore } from "~features/checkout/legacy/helpers/paymentOptions.store";
import { useGetPaymentOptions } from "~features/checkout/standard/hooks/useGetPaymentOptions";
import { CustomFieldOuput, OrderType } from "~graphql/sdk";
import { GetXeroConfigurationQueryDocument } from "~graphql/typed-document-nodes";
import { useAccount, useCheckout, useOrganization } from "~hooks";
import { useQuery } from "~hooks/useQuery";
import { useOrderContext } from "./OrderContext";
import useCallbackQueue from "./useCallbackQueue";
import { useAnalyticsAndTracking } from "./useAnalyticsAndTracking";
import { useUnsavedChangesWarning } from "./useUnsavedChangesWarning";
import { useQueryParamTracking } from "./useQueryParamTracking";
import { usePostHog } from "posthog-js/react";

export interface SinglePageCheckoutContextProps extends ReturnType<
  typeof useCheckout
> {
  isQueueProcessing: boolean;
  isQueueSubmitting: boolean;
  addToQueue: (id: string, callback: () => Promise<void>) => void;
  cancelOrder: () => void;
  handleLeavePage: (shouldExpireOrder?: boolean) => void;
  showLeaveModal: boolean;
  setShowLeaveModal: React.Dispatch<React.SetStateAction<boolean>>;
  isTicketTransferOrder: boolean;
  isRenewMembershipsOrder: boolean;
  payments: ReturnType<typeof useGetPaymentOptions>;
  customFields: CustomFieldOuput[];
  setCustomFields: React.Dispatch<React.SetStateAction<CustomFieldOuput[]>>;
  hasPaidInFullWithPoints: boolean;
  hasPaidInFullWithCredits: boolean;
  willSendInvoiceForHoldOrder: boolean;
  hasXeroIntegration: boolean;
  transactionInProgress: boolean;
  showPurchaseRestrictionsModal: boolean;
  setShowPurchaseRestrictionsModal: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  purchaseRestrictionMessage: JSX.Element | undefined;
  handleUpdateOrderError: (error?: ExtendedGraphQLError) => boolean;
  submitDisabled: boolean;
}

export const SinglePageCheckoutContext = createContext<
  SinglePageCheckoutContextProps | undefined
>(undefined);

/** Page-level provider for checkouts including single-page, express & Pre-auth */
export const SinglePageCheckoutProvider: FC = ({ children }) => {
  const checkout = useCheckout();
  const { totals } = checkout;
  const {
    order,
    redirectConfig,
    isBackOfficeOrder,
    isPOSOrder,
    pointName,
    eventId,
    membershipId,
    hasBuyerEmail,
  } = useOrderContext();
  const { organization } = useOrganization();
  const postHog = usePostHog();

  const router = useRouter();

  const {
    addToQueue,
    isProcessing: isQueueProcessing,
    processingId,
  } = useCallbackQueue({
    deduplicationStrategy: "keep-last",
    onQueueFinished: async () => checkout.mutate(),
  });

  const [transactionInProgress, setTransactionInProgress] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showPurchaseRestrictionsModal, setShowPurchaseRestrictionsModal] =
    useState(false);
  const [purchaseRestrictionMessage, setPurchaseRestrictionMessage] = useState<
    JSX.Element | undefined
  >(undefined);

  const [customFields, setCustomFields] = useState<CustomFieldOuput[]>(
    (order?.event?.customFields ?? []).filter((customField) =>
      customField.targetArea.includes("order")
    )
  );

  const requiredCustomFieldsAreChecked = (customFields ?? [])
    .filter((field) => field.isRequired)
    .every((field) => field.value === true);

  const isTicketTransferOrder = order?.orderType === OrderType.TicketTransfer;
  const isRenewMembershipsOrder =
    order?.orderType === OrderType.RenewMemberships;

  // Handle analytics tracking and Facebook Pixel
  useAnalyticsAndTracking();

  // Handle URL query param tracking for channel and step
  useQueryParamTracking({
    channel: order?.channel,
    hasBuyerEmail,
  });

  const expireOrder = useCallback(async () => {
    if (!order?.id || !organization?.id) return;
    try {
      await sdk({ orgId: organization.id }).expireOrder({ id: order.id });
    } catch (e) {
      console.error("Failed to expire order:", e);
    }
  }, [order?.id, organization?.id]);

  const handleLeavePage = useCallback(
    (shouldExpireOrder = true) => {
      if (shouldExpireOrder) {
        void expireOrder();
      }
      postHog?.stopSessionRecording();
      void router.push(redirectConfig);
    },
    [expireOrder, router, redirectConfig, postHog]
  );

  const handleUpdateOrderError = useCallback(
    (error?: ExtendedGraphQLError): boolean => {
      if (!error) return false;

      // 987 indicates unmet purchase restrictions
      if (error.statusCode === 987) {
        setPurchaseRestrictionMessage(
          <div dangerouslySetInnerHTML={{ __html: error.message }}></div>
        );
        setShowPurchaseRestrictionsModal(true);
        return true; // Error was handled
      }
      return false; // Error not handled
    },
    []
  );

  const cancelOrder = () => {
    // Show modal for confirmation
    setShowLeaveModal(true);
  };

  const { activeBannerHeight } = useShowHeaderBanner();

  const { isAdmin, userRoles, user, isSalesOutlet, isEventManager } =
    useAccount();

  // Handle unsaved changes warning and browser navigation
  useUnsavedChangesWarning({
    isRenewMembershipsOrder,
    orderStatus: order?.status,
    setShowLeaveModal,
  });

  const canSpendPoints =
    (order?.membership?.canUsePoints || order?.event?.canUsePoints) && !!user;

  const canCreateHoldOrder =
    (isAdmin || isEventManager || order?.release?.allowHoldOrders) &&
    !isSalesOutlet;

  const hasPaidInFullWithPoints =
    totals.usedPoints === totals.totalWithTransactionFee &&
    totals.usedPoints > 0;

  const hasPaidInFullWithCredits =
    totals.totalWithTransactionFee - totals.usedCredits <= 0 &&
    totals.usedCredits > 0;

  const { data: xeroConfiguration } = useQuery(
    isAdmin && GetXeroConfigurationQueryDocument
  );

  const payments = useGetPaymentOptions({
    isAdmin,
    userRoles,
    totals: totals,
    gateways: checkout.gateways,
    canSpendPoints,
    canCreateHoldOrder,
    isPOSUser: isPOSOrder,
    posPaymentOptions: checkout.posData?.paymentOptions ?? {},
    pointName,
    hasPaidInFullWithCredits,
    isBackOfficeOrder,
    backendPaymentOptions: checkout.paymentplanData,
    paymentType: "full",
    selectedId: checkout.method,
    selectedPartPaymentId: checkout.partPaymentMethod,
    onClick: (selectedMethod: string, selectedPartMethod?: string) => {
      checkout.setMethod(selectedMethod);
      checkout.setPartPaymentMethod(selectedPartMethod);

      // Set partialPaymentMethod for split payments (e.g., credits + card)
      // This is different from partPaymentMethod which is for payment plan variants
      checkout.setPartialPaymentMethod(selectedPartMethod);

      // Update the payment options store
      // If a payment plan is selected (selectedPartMethod), look in partPaymentOptions
      // Otherwise, use the main selected method option from paymentOptions
      const selectedOption = selectedPartMethod
        ? payments.partPaymentOptions?.find(
            (o) => o.value === selectedPartMethod
          )
        : payments.paymentOptions?.find((o) => o.value === selectedMethod);

      usePaymentOptionsStore.setState({
        selectedPaymentOption: selectedOption,
      });
    },
    showHiddenFees: checkout.showHiddenFees,
    totalForBNPL: checkout.totals.orderTotal,
    eventId,
    membershipId,
    orderId: order?.id,
  });

  function scrollToPaymentPlanOptions() {
    const desktopHeader = document.getElementById("checkout-desktop-header");
    const headerHeight = desktopHeader ? desktopHeader.offsetHeight : 0;
    const offset = activeBannerHeight + headerHeight + 10;

    setTimeout(() => {
      const el = document.getElementById("paymentPlanOptions");
      if (el) {
        const rect = el.getBoundingClientRect();
        window.scrollTo({
          top: window.scrollY + rect.top - offset,
          behavior: "smooth",
        });
      }
    }, 200);
  }

  // If Xero config option sendFlicketInvoice is false, then we don't attach the invoice to the email.
  // Defaults to true if not set.
  //! TODO its not great to have to make this network call just for one word in the UI
  const willSendInvoiceForHoldOrder =
    xeroConfiguration?.getXeroConfiguration?.sendFlicketInvoice !== undefined
      ? xeroConfiguration.getXeroConfiguration.sendFlicketInvoice
      : true;
  const hasXeroIntegration = !!xeroConfiguration?.getXeroConfiguration;

  const submitDisabled = isBackOfficeOrder
    ? false
    : !requiredCustomFieldsAreChecked;

  return (
    <SinglePageCheckoutContext.Provider
      value={{
        ...checkout,
        totals, // Override with payment plan fee applied
        isQueueProcessing,
        isQueueSubmitting: processingId === "submit",
        addToQueue,
        cancelOrder,
        handleLeavePage,
        showLeaveModal,
        setShowLeaveModal,
        isTicketTransferOrder,
        isRenewMembershipsOrder,
        payments,
        customFields,
        setCustomFields,
        submitDisabled,
        hasPaidInFullWithPoints,
        hasPaidInFullWithCredits,
        willSendInvoiceForHoldOrder,
        hasXeroIntegration,
        transactionInProgress,
        showPurchaseRestrictionsModal,
        setShowPurchaseRestrictionsModal,
        purchaseRestrictionMessage,
        handleUpdateOrderError,
      }}
    >
      {children}
    </SinglePageCheckoutContext.Provider>
  );
};

export const useSinglePageCheckoutContext = () => {
  const ctx = useContext(SinglePageCheckoutContext);
  if (!ctx)
    throw new Error(
      "SinglePageCheckoutContext must be used within SinglePageCheckoutProvider"
    );
  return ctx;
};
